import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { PageBodyContainer } from '@repo/ui/components/layouts/page-body-container.tsx';

import { PageLoader } from '~/components/loader.tsx';
import { isMemberProfileComplete } from '~/domains/member/utils/is-member-profile-complete.ts';
import {
  useCurrentUserMembersQuery,
  useReconcileMemberMutation,
} from '~/graphql/types-and-hooks.ts';
import { useAppConfig } from '~/utils/use-app-config.ts';
import { usePathLocale } from '~/utils/use-path-locale.ts';

export const Route = createFileRoute('/$pathLocale/_layout/_auth/')({
  component: RouteComponent,
});

const maxRetries = 2;
const retryDelay = 2000;

function RouteComponent() {
  const { data, loading, refetch } = useCurrentUserMembersQuery();
  const [reconcileMember, { loading: reconciling }] = useReconcileMemberMutation({
    errorPolicy: 'ignore',
  });
  const { t } = useTranslation(['members']);
  const appConfig = useAppConfig();
  const pathLocale = usePathLocale();
  const retriesRef = useRef(0);
  const reconciledRef = useRef(false);

  useEffect(() => {
    if (loading || reconciling) return;
    if (data?.currentUserMembers.length) return;

    if (retriesRef.current < maxRetries) {
      const timer = setTimeout(() => {
        retriesRef.current++;
        refetch();
      }, retryDelay);
      return () => clearTimeout(timer);
    }

    if (!reconciledRef.current) {
      reconciledRef.current = true;
      reconcileMember().catch((err) => console.error('Reconciliation failed:', err));
    }
  }, [data, loading, refetch, reconcileMember, reconciling]);

  if (loading || !data || data.currentUserMembers.length === 0) {
    return (
      <div className="h-screen flex items-center">
        <PageLoader className="size-16" />
      </div>
    );
  }

  const member = data.currentUserMembers[0];
  const isCompleted = isMemberProfileComplete(member);

  if (!isCompleted) {
    return <Navigate to="/$pathLocale/complete-profile" params={{ pathLocale }} />;
  }

  return (
    <PageBodyContainer>
      <div className="p-4">
        <div className="text-center w-full pb-8">
          <h2 className="font-semibold text-lg">{t('members:download-app')}</h2>
        </div>

        <div className="flex flex-col gap-4 w-full justify-center items-center">
          {appConfig.iosUrl && (
            <a href={appConfig.iosUrl} rel="noreferrer" target="_blank">
              <img alt="Apple app store" height={67} width={200} src="/apple-app-store.png" />
            </a>
          )}

          {appConfig.androidUrl && (
            <a href={appConfig.androidUrl} rel="noreferrer" target="_blank">
              <img alt="Google play store" height={59} src="/google-play-store.png" width={200} />
            </a>
          )}
        </div>
      </div>
    </PageBodyContainer>
  );
}
